import { useEffect, useState } from "react";
import {
  AccountingSystemForCalculatingFinancialStatistics,
  CompareBy,
  FinancialComparison,
  PaymentsService,
} from "client/openapi";
import { Range } from "./data";

export type ComparisonQuery = {
  orgId: number;
  range: Range;
  compared: Range | null;
  basis: AccountingSystemForCalculatingFinancialStatistics;
  // None chosen means all subjects.
  subjectIds: number[];
};

// The summary, the chart and the table often ask for the same days at once;
// they share one request, kept for a minute.
const KEEP_MS = 60_000;
const cache = new Map<
  string,
  { at: number; promise: Promise<FinancialComparison> }
>();

function load(query: ComparisonQuery): Promise<FinancialComparison> {
  const key = JSON.stringify(query);
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < KEEP_MS) return hit.promise;
  const promise = PaymentsService.getOrgFinancialComparison({
    orgId: query.orgId,
    start: query.range[0],
    until: query.range[1],
    by: CompareBy.DAY,
    accountingSystem: query.basis,
    compareStart: query.compared?.[0],
    compareUntil: query.compared?.[1],
    subjectIds: query.subjectIds.length ? query.subjectIds : undefined,
  });
  cache.set(key, { at: Date.now(), promise });
  promise.catch(() => cache.delete(key));
  return promise;
}

/**
 * Both periods by day, for Insights to group as it shows them. On failure the
 * data is cleared and `failed` says why: the server's reason when it gives one.
 */
export function useComparison(query: ComparisonQuery) {
  const [data, setData] = useState<FinancialComparison>();
  const [failed, setFailed] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const key = JSON.stringify(query);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setFailed(null);
    load(query)
      .then((result) => live && setData(result))
      .catch((error) => {
        if (!live) return;
        const detail = error?.body?.detail;
        setData(undefined);
        setFailed(
          typeof detail === "string" ? detail : "Couldn't load this comparison."
        );
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // `key` is the query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { data, failed, loading };
}

/**
 * Each viewer's Insights choices, per organization; local storage may be
 * unavailable. (v2: choices made while testing don't override the defaults.)
 */
export function remembered<T>(orgId: number, name: string, fallback: T): T {
  try {
    const all = JSON.parse(
      localStorage.getItem(`financials-insights-v2:${orgId}`) || "{}"
    );
    return name in all ? all[name] : fallback;
  } catch {
    return fallback;
  }
}

export function remember(orgId: number, name: string, value: unknown) {
  try {
    const key = `financials-insights-v2:${orgId}`;
    const all = JSON.parse(localStorage.getItem(key) || "{}");
    localStorage.setItem(key, JSON.stringify({ ...all, [name]: value }));
  } catch {
    // Not remembered this time; the page still works.
  }
}
